import { CancelAndEditActions } from '@common/components/forms/CancelAndEditActions';
import { FormInput, ToggleButton } from '@common/components/forms/controls';
import { PageCard, PageCardProps } from '@common/components/layout';
import { YesNoConfirmModal } from '@common/components/modals';
import { HospitalHourOptions, byDayOfWeek } from '@common/components/time/LocationHours';
import { useUnsavedChangesWarning } from '@common/hooks';
import { HospitalModel } from '@common/models';
import { WeekDay } from '@common/models/Enums';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { UseFormHandleSubmit, useFieldArray, useFormContext } from 'react-hook-form';
import { IHospitalHoursGroup } from '.';
import { RichTextDisplay } from '@common/components/richTextBox/RichTextDisplay';
type IProps = {
    hospitalHoursGroup: IHospitalHoursGroup
    onUpdateHospital: (data: HospitalModel) => void
    clearHospitalHoursConfirmation: () => void
    setHospitalHoursChangesConfirmed: () => void
} & PageCardProps

const ChangeHoursNoticeModal = observer(({ setHospitalHoursChangesConfirmed, onUpdateHospital, hospitalHoursGroup, handleSubmit, clearHospitalHoursConfirmation }: IProps & { handleSubmit: UseFormHandleSubmit<Omit<HospitalModel, "defaultScheduleTemplate">> }) => {
    const confirmHospitalHours = (data: HospitalModel) => {
        setHospitalHoursChangesConfirmed();
        onUpdateHospital(data);
    }
    const hoursConfirmationMessage = () => {
        let text = "We noticed that you";
        text += hospitalHoursGroup.hospitalClosureChanged
            ? " have chosen to close the hospital for one or more days" : "";
        text += (hospitalHoursGroup.hospitalClosureChanged && hospitalHoursGroup.hospitalHoursExceedWorkday)
            ? " and" : "";
        text += hospitalHoursGroup.hospitalHoursExceedWorkday
            ? " have selected hours outside the normal work day" : "";
        text += ". Would you like to continue?";
        return text;
    }
    return <YesNoConfirmModal
        visible={hospitalHoursGroup.hospitalHoursVisible}
        title="Confirm hospital hours changes"
        description={hoursConfirmationMessage()}
        busy={hospitalHoursGroup.hospitalHoursConfirmationLoading}
        onConfirm={handleSubmit(confirmHospitalHours)}
        onCancel={clearHospitalHoursConfirmation}
    />
})

export const LocationHoursDetail = observer(({ onUpdateHospital, setHospitalHoursChangesConfirmed, hospitalHoursGroup, clearHospitalHoursConfirmation, ...cardProps }: IProps) => {
    const {
        register, handleSubmit, control, watch, reset,setValue, formState: {isDirty }
    } = useFormContext<Omit<HospitalModel, "defaultScheduleTemplate">>() //<- omit defaultScheduleTemplate because it causes a circular reference.


    const { fields } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: "hospitalHours", // unique name for your Field Array
        // keyName: "id", default to "id", you can change the key name
    });

    const formHours = watch("hospitalHours");
    const isOutOfOfficeEnabled = watch("isOutOfOfficeEnabled");
    const outOfOfficeMessage = watch("outOfOfficeMessage");
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [locationDetailsDirty, setLocationDetailsDirty] = React.useState<boolean>(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);

    const cancelingEditing = () => {
        if (locationDetailsDirty)
            setShowCancelConfirm(true);
        else
            cancelEditing();
    }

    const cancelEditing = () => {
        setIsEditing(false);
        setShowCancelConfirm(false);
        reset()
    }

    const unsavedChangesPrompt = useUnsavedChangesWarning(locationDetailsDirty);
    useEffect(() => {
        if (setLocationDetailsDirty)
            setLocationDetailsDirty(isDirty);
    }, [isDirty, setLocationDetailsDirty]);

    useEffect(() => {

        if(!isOutOfOfficeEnabled){
        setValue('outOfOfficeMessage','');
    }
    }, [isOutOfOfficeEnabled,setValue])

    const formId = 'locationDetailsHoursForm'
    const readonly = !isEditing;

    return (
        <>
            <ChangeHoursNoticeModal {...{ setHospitalHoursChangesConfirmed, onUpdateHospital, hospitalHoursGroup, handleSubmit, clearHospitalHoursConfirmation }} />
            {unsavedChangesPrompt}
            <YesNoConfirmModal
                visible={showCancelConfirm}
                title="Cancel Changes"
                description="You have unsaved changes, you are sure you want to cancel?"
                busy={false}
                cancelButtonText='No'
                onConfirm={() => cancelEditing()}
                onCancel={() => { setIsEditing(true); setShowCancelConfirm(false) }}
            />
            <PageCard className="mt-4" title="Hours of Operation" actionElement={
                <CancelAndEditActions
                    formId={formId}
                    editText="Edit Details"
                    saveText="Save Details"
                    isEditing={isEditing}
                    saveClicked={(e) => {
                        if (!isEditing) {
                            e?.preventDefault();
                            return false;
                        }
                        return true;
                    }}
                    cancelClicked={cancelingEditing}
                    editClicked={() => {
                        if (!isEditing) {
                            setIsEditing(true);
                        }
                    }}
                />}
                {...cardProps}
            >
                <Form id={formId} className="input-form" onSubmit={handleSubmit(onUpdateHospital)}>
                    <div className="hours-operation d-inline-flex d-xl-inline-flex flex-column">
                        {fields
                            .sort(byDayOfWeek)
                            .map((field: any, index: number) => (
                                <div className="d-flex flex-row hours-operation-row py-2" key={field.id}>
                                    <div className="d-flex flex-row flex-fill justify-content-start align-items-center">
                                        <Form.Label className="d-flex day-label text-left fw-bold me-2 text-uppercase">
                                            {WeekDay.getShortName(index)}
                                        </Form.Label>
                                        <ToggleButton className="d-inline-flex me-2" {...register(`hospitalHours.${index}.isOpen` as const)} aria-label={`open on ${WeekDay.getShortName(index)}?`} noSlider={readonly} defaultChecked={formHours[index].isOpen} />
                                        <span className="d-inline-flex status-label me-2">{formHours[index].isOpen ? 'Open' : 'Closed'}</span>
                                        <div className="hour-selection d-flex flex-row justify-content-center">
                                            <Form.Select style={{ minWidth: 100 }} className={classNames("time", { "color-gray": !formHours[index].isOpen })} {...register(`hospitalHours.${index}.openTime` as const)} aria-label={`${WeekDay.getShortName(index)} open time`} disabled={(!formHours[index].isOpen) || readonly} defaultValue={formHours[index].openTime}>
                                                {HospitalHourOptions}
                                            </Form.Select>
                                            <span className="divider align-self-center px-2">-</span>
                                            <Form.Select style={{ minWidth: 100 }} className={classNames("time", { "color-gray": !formHours[index].isOpen })} {...register(`hospitalHours.${index}.closeTime` as const)} aria-label={`${WeekDay.getShortName(index)} close time`} disabled={(!formHours[index].isOpen) || readonly} defaultValue={formHours[index].closeTime}>
                                                {HospitalHourOptions}
                                            </Form.Select>
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                    <div className="d-flex flex-row flex-fill justify-content-start align-items-center mt-3">
                        <Form.Label className="d-flex day-label text-left fw-bold me-2 text-uppercase mt-3">
                            Out of Office Message
                            </Form.Label>
                        <ToggleButton className="d-inline-flex me-2" {...register('isOutOfOfficeEnabled')} noSlider={readonly} defaultChecked={isOutOfOfficeEnabled} />
                        <span className={classNames("d-inline-flex status-label mt-3 me-2",{ "color-gray": !isOutOfOfficeEnabled })}>{isOutOfOfficeEnabled ? 'Enabled' : 'Disabled'}</span>                        
                    </div>
                    <div className="d-flex flex-row flex-fill justify-content-start align-items-center mt-2">
                    <Form.Label  className={classNames("d-flex flex-row day-label text-left fw-bold me-2 text-uppercase", { "color-gray": !isOutOfOfficeEnabled })}>
                            message
                            </Form.Label>                           
                    </div>
                    <div className="mt-2">
                    {!readonly &&
                        <FormInput control={control}
                        type="textarea"
                        label="" controlId='outOfOfficeMessage'
                        fieldName="outOfOfficeMessage" 
                        readOnly={!isOutOfOfficeEnabled}
                        isRequired={isOutOfOfficeEnabled}
                        rows={3}
                        />
                    }
                    {readonly && <RichTextDisplay value={isOutOfOfficeEnabled ?outOfOfficeMessage:''} />}
                    </div>                  
                </Form>
            </PageCard>
        </>
    )
});
